import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUserData } from '../../hooks/useUserData';
import { useAdmin } from '../../hooks/useAdmin';
import { subscribeToActiveAvailableQuests } from '../../services/questService';
import { ICONS } from '../../icons';
import DashboardSidebar from './DashboardSidebar';
import DashboardHome from './DashboardHome';
import ActiveQuest from './ActiveQuest';
import QuestAccordion from './QuestAccordion';
import ProfilePage from './ProfilePage';
import useMouseGlow from '../../hooks/useMouseGlow';
import appLogo from '../../assets/png/appLogo.png';
import '../../dashboard-sidebar.css';

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ['home', 'current', 'available', 'questlog', 'profile'];
  const paramTab = searchParams.get('tab');
  const activeTab = validTabs.includes(paramTab) ? paramTab : 'home';
  const setActiveTab = (tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab === 'home') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      // Clear sub-params when switching tabs
      next.delete('quest');
      next.delete('segment');
      next.delete('filter');
      return next;
    }, { replace: true });
  };

  const [collapsed, setCollapsed] = useState(() => window.innerWidth <= 1024);
  const [error, setError] = useState('');

  // Auto-collapse sidebar on resize
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const handler = (e) => setCollapsed(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const [availableQuests, setAvailableQuests] = useState([]);
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    loading: userDataLoading,
    error: userDataError,
    userData,
    questRequests,
    getFormattedStats,
    getFormattedActiveQuest,
    getFormattedQuestHistory,
    getPendingQuestRequests,
    getApprovedQuestRequests,
    getRejectedQuestRequests,
    hasActiveQuest,
    completeQuest
  } = useUserData();

  const { createQuestRequest, isAdmin } = useAdmin();

  // Global mouse glow — single listener for all glow-eligible cards
  useMouseGlow('.dashboard-sidebar, .sidebar-item.active, .sidebar-item.prominent, .sidebar-brand, .sidebar-profile-header-section, .sidebar-footer, .sidebar-divider, .dh-bento-identity, .dh-bento-xp, .dh-xp-animal, .dh-mission-hero, .dh-mission-card, .dh-quest-tile, .dh-pending-strip, .dh-view-all, .qp-quest-tile, .qp-request-card, .qp-page .filter-tab, .qp-tile-cta, .cq-hero, .cq-objective, .resource-link, .ql-segment, .sidebar-logout-btn, .qdv-hero, .qdm-sq-item, .qdm-requirements');

  // Lift available quests subscription to Dashboard for sidebar badges + home tab
  useEffect(() => {
    const unsubscribe = subscribeToActiveAvailableQuests((quests) => {
      setAvailableQuests(quests);
    });
    return () => unsubscribe();
  }, []);

  // If viewing active quest tab but no active quest, redirect to home
  useEffect(() => {
    if (activeTab === 'current' && !hasActiveQuest()) {
      setActiveTab('home');
    }
  }, [activeTab, hasActiveQuest]);

  const handleLogout = async () => {
    try {
      setError('');
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      setError(t('dashboard.failedToLogout'));
    }
  };

  const handleStartQuest = async (questData) => {
    try {
      setError('');
      if (hasActiveQuest()) {
        setError('You already have an active quest. Complete or abandon your active quest before starting a new one.');
        return;
      }
      const pendingReqs = getPendingQuestRequests();
      if (pendingReqs.length > 0) {
        setError('You already have a quest request pending approval. Please wait for admin approval before requesting another quest.');
        return;
      }
      if (currentUser) {
        await createQuestRequest(currentUser.uid, questData.id, questData);
        toast.success(t('quests.approval.requestSent'), {
          description: 'Your quest request has been submitted and is awaiting admin approval.',
          duration: 5000,
        });
      } else {
        setError('User not authenticated');
      }
    } catch (error) {
      console.error('Quest request error:', error);
      setError(t('dashboard.failedToStartQuest'));
    }
  };

  const handleCompleteStep = async (stepId) => {
    try {
      setError('');
      const activeQuest = getFormattedActiveQuest();
      if (activeQuest) {
        await completeQuest(activeQuest.id, activeQuest.value || 10);
      }
    } catch (error) {
      console.error('Complete step error:', error);
      setError(t('dashboard.failedToCompleteStep'));
    }
  };

  // Loading state
  if (userDataLoading) {
    return (
      <div className="dashboard-layout">
        <div className="app-logo-breath-container">
          <img src={appLogo} alt="StreamQuest Logo" className="app-logo-breath" />
        </div>
      </div>
    );
  }

  const stats = getFormattedStats();
  const activeQuest = getFormattedActiveQuest();
  const questHistory = getFormattedQuestHistory();
  const pendingRequests = getPendingQuestRequests();
  const approvedRequests = getApprovedQuestRequests();
  const rejectedRequests = getRejectedQuestRequests();

  // Tab definitions with live counts
  const tabs = [
    ...(hasActiveQuest() ? [{ id: 'current', label: 'Active Quest', icon: ICONS.BOLT, prominent: true }] : []),
    { id: 'home', label: 'Home', icon: ICONS.COMPASS },
    { id: 'available', label: 'Available Quests', icon: ICONS.MAP, count: availableQuests.length },
    { id: 'questlog', label: 'Quest Log', icon: ICONS.SCROLL },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <DashboardHome
            userData={userData}
            stats={stats}
            activeQuest={activeQuest}
            availableQuests={availableQuests}
            pendingRequests={pendingRequests}
            questHistory={questHistory}
            onTabChange={setActiveTab}
          />
        );
      case 'available':
        return (
          <QuestAccordion
            section="available"
            availableQuests={availableQuests}
            pendingRequests={pendingRequests}
            onStartQuest={handleStartQuest}
            hasActiveQuest={hasActiveQuest()}
          />
        );
      case 'current':
        return (
          <ActiveQuest
            activeQuest={activeQuest}
            onCompleteStep={handleCompleteStep}
            userStats={stats}
          />
        );
      case 'questlog':
        return (
          <QuestAccordion
            section="questlog"
            questRequests={questRequests}
            pendingRequests={pendingRequests}
            approvedRequests={approvedRequests}
            rejectedRequests={rejectedRequests}
            questHistory={questHistory}
          />
        );
      case 'profile':
        return (
          <ProfilePage
            userData={userData}
            stats={stats}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-layout">
      <DashboardSidebar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        userData={userData || {}}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(!collapsed)}
        onLogout={handleLogout}
      />
      <main className="dashboard-main">
        {(error || userDataError) && (
          <div className="error" style={{ marginBottom: 16 }}>
            {error || userDataError}
          </div>
        )}
        {renderContent()}
      </main>
    </div>
  );
}
